import { sameJson } from "./same-json.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type DoorLayer, type DoorTarget, type JsonValue, type MapDocument } from "./types.ts";

/**
 * Door layers (ADR-122 decision 2.1): the door a cell holds, the map's doors folded across its door layers, and
 * the edits the door commands apply. A layer's `doors` is the builder's list; a cell holds 0 or the 1-based place
 * of its door in it, and every edit names a door by id, so adding or removing a door renumbers the cells and no
 * byte depends on the order edits arrived in.
 */

/**
 * The most doors one layer lists: what a cell's byte indexes.
 */
export const MAX_DOORS_PER_LAYER = 255;

/**
 * A door's name, destination and fields: the part `updateDoor` changes.
 */
export interface DoorState {
  readonly name: string;
  readonly to: DoorTarget;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * One cell of a `paintDoor` stroke: the id of the door it held and of the one it gets, null for none.
 */
export interface DoorCellChange {
  readonly index: number;
  readonly before: string | null;
  readonly after: string | null;
}

/**
 * A map's doors folded across its door layers: 16 bits per collision cell, 0 for none or a 1-based index into
 * `doors`, which lists the doors the grid uses in the order a row-major scan first meets them.
 */
export interface FoldedDoors {
  readonly doors: readonly DoorDefinition[];
  readonly cells: Uint16Array;
}

/**
 * The door a cell of a door layer holds.
 *
 * @param layer The layer.
 * @param index The cell's index in the collision grid.
 * @returns The door, or null for a cell holding none.
 * @throws RangeError when the index lies outside the grid.
 */
export function doorAt(layer: DoorLayer, index: number): DoorDefinition | null {
  if (!Number.isInteger(index) || index < 0 || index >= layer.cells.length) {
    throw new RangeError(`cell index ${index} is outside the grid of ${layer.cells.length}`);
  }
  const value = layer.cells[index] as number;
  return value === 0 ? null : (layer.doors[value - 1] ?? null);
}

/**
 * A door's state, the part `updateDoor` carries.
 *
 * @param door The door.
 * @returns Its name, destination and fields.
 */
export function doorState(door: DoorDefinition): DoorState {
  return { name: door.name, to: door.to, fields: door.fields };
}

/**
 * Folds a map's door layers into one grid: where two paint a cell the later layer, the one drawn above, wins, and a
 * cell without a door lets the layer below show through. Hidden layers count, since hidden is view state.
 *
 * @param map The map.
 * @returns The folded doors.
 */
export function foldDoors(map: MapDocument): FoldedDoors {
  const cells = new Uint16Array(map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE);
  const all: DoorDefinition[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "door" || layer.doors.length === 0) {
      continue;
    }
    const offset = all.length;
    all.push(...layer.doors);
    const length = Math.min(cells.length, layer.cells.length);
    for (let i = 0; i < length; i += 1) {
      const value = layer.cells[i] as number;
      if (value !== 0) {
        cells[i] = offset + value;
      }
    }
  }
  const remap = new Uint16Array(all.length + 1);
  const doors: DoorDefinition[] = [];
  for (let i = 0; i < cells.length; i += 1) {
    const value = cells[i] as number;
    if (value === 0) {
      continue;
    }
    if (remap[value] === 0) {
      doors.push(all[value - 1] as DoorDefinition);
      remap[value] = doors.length;
    }
    cells[i] = remap[value] as number;
  }
  return { doors, cells };
}

/**
 * Checks a value a door cell may hold: a non-empty door id, or null.
 *
 * @param value The value.
 * @param what What holds or writes it, the start of the message.
 * @returns The value.
 * @throws Error when the value is neither.
 */
export function doorValue(value: unknown, what: string): string | null {
  if (value === null || (typeof value === "string" && value !== "")) {
    return value;
  }
  throw new Error(`${what} a door id or none, not ${JSON.stringify(value)}`);
}

/**
 * The byte a door id takes in a layer.
 *
 * @param layer The layer.
 * @param id The door's id, or null.
 * @returns The byte, 0 for null.
 * @throws Error when the layer lists no such door.
 */
function byteOf(layer: DoorLayer, id: string | null): number {
  if (id === null) {
    return 0;
  }
  const index = layer.doors.findIndex((door) => door.id === id);
  if (index < 0) {
    throw new Error(`door layer ${layer.id} lists no door ${id}`);
  }
  return index + 1;
}

/**
 * Names a cell's value for a message.
 *
 * @param id The door's id, or null.
 * @returns The id, or `none`.
 */
function named(id: string | null): string {
  return id ?? "none";
}

/**
 * Writes cell edits into a copy of a door layer. Each cell must hold its `before` door, and each door named must
 * be one the layer lists.
 *
 * @param layer The layer.
 * @param edits The cells, each by index with its door before and after.
 * @returns The layer edited.
 * @throws Error when an index lies outside the grid, a cell does not hold its `before` door, or a door is not the
 *   layer's.
 */
export function editDoorCells(layer: DoorLayer, edits: readonly DoorCellChange[]): DoorLayer {
  const length = layer.cells.length;
  for (const edit of edits) {
    if (!Number.isInteger(edit.index) || edit.index < 0 || edit.index >= length) {
      throw new Error(`cell index ${edit.index} is outside the grid of ${length}`);
    }
    doorValue(edit.before, "a door cell holds");
    doorValue(edit.after, "a door cell holds");
    const held = doorAt(layer, edit.index)?.id ?? null;
    if (held !== edit.before) {
      throw new Error(`cell ${edit.index} holds ${named(held)}, not the recorded before value ${named(edit.before)}`);
    }
  }
  const cells = new Uint8Array(layer.cells);
  for (const edit of edits) {
    cells[edit.index] = byteOf(layer, edit.after);
  }
  return { ...layer, cells };
}

/**
 * Fills listed cells of a door layer holding one door, or none, with another.
 *
 * @param layer The layer.
 * @param indices The cells.
 * @param before The door each holds, or null.
 * @param after The door each gets, or null.
 * @returns The layer filled.
 * @throws Error when a value is not a door id or null, an index lies outside the grid, a cell does not hold
 *   `before`, or a door is not the layer's.
 */
export function fillDoorCells(layer: DoorLayer, indices: Readonly<Uint32Array>, before: unknown, after: unknown): DoorLayer {
  const from = doorValue(before, "a door fill writes");
  const to = doorValue(after, "a door fill writes");
  const held = byteOf(layer, from);
  const value = byteOf(layer, to);
  const length = layer.cells.length;
  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i] as number;
    if (index >= length) {
      throw new Error(`fill index ${index} is outside the grid of ${length}`);
    }
    if (layer.cells[index] !== held) {
      throw new Error(`cell ${index} holds ${named(doorAt(layer, index)?.id ?? null)}, not the recorded before value ${named(from)}`);
    }
  }
  const cells = new Uint8Array(layer.cells);
  for (let i = 0; i < indices.length; i += 1) {
    cells[indices[i] as number] = value;
  }
  return { ...layer, cells };
}

/**
 * Inserts a door into a layer's list, unpainted, renumbering the cells of the doors after it.
 *
 * @param layer The layer.
 * @param index The place the door takes.
 * @param door The door.
 * @returns The layer with the door.
 * @throws Error when the index is outside the list, or the layer already lists `MAX_DOORS_PER_LAYER` doors.
 */
export function insertDoor(layer: DoorLayer, index: number, door: DoorDefinition): DoorLayer {
  if (!Number.isInteger(index) || index < 0 || index > layer.doors.length) {
    throw new Error(`door insert index ${index} is outside [0, ${layer.doors.length}]`);
  }
  if (layer.doors.length >= MAX_DOORS_PER_LAYER) {
    throw new Error(`door layer ${layer.id} already lists ${MAX_DOORS_PER_LAYER} doors, the most a layer holds`);
  }
  const cells = Uint8Array.from(layer.cells, (value) => (value > index ? value + 1 : value));
  return { ...layer, doors: [...layer.doors.slice(0, index), door, ...layer.doors.slice(index)], cells };
}

/**
 * Takes an unpainted door out of a layer's list, renumbering the cells of the doors after it.
 *
 * @param layer The layer.
 * @param index The door's place.
 * @param door The door the place must hold.
 * @returns The layer without the door.
 * @throws Error when the place holds another door, or a cell still holds this one.
 */
export function withdrawDoor(layer: DoorLayer, index: number, door: DoorDefinition): DoorLayer {
  const held = layer.doors[index];
  if (held === undefined || !sameJson(held, door)) {
    throw new Error(`door at ${index} of layer ${layer.id} does not match the carried door`);
  }
  const byte = index + 1;
  if (layer.cells.includes(byte)) {
    throw new Error(`door ${door.id} is still painted on layer ${layer.id}; clear its cells first`);
  }
  const cells = Uint8Array.from(layer.cells, (value) => (value > byte ? value - 1 : value));
  return { ...layer, doors: [...layer.doors.slice(0, index), ...layer.doors.slice(index + 1)], cells };
}

/**
 * Replaces a door's name, destination and fields, checking what it held first.
 *
 * @param layer The layer.
 * @param doorId The door.
 * @param before The state the door must hold.
 * @param after The state it gets.
 * @returns The layer with the door changed.
 * @throws Error when the layer lists no such door, or it does not hold `before`.
 */
export function changeDoor(layer: DoorLayer, doorId: string, before: DoorState, after: DoorState): DoorLayer {
  const index = layer.doors.findIndex((door) => door.id === doorId);
  const held = layer.doors[index];
  if (held === undefined) {
    throw new Error(`door layer ${layer.id} lists no door ${doorId}`);
  }
  if (!sameJson(doorState(held), before)) {
    throw new Error(`door ${doorId} does not hold the recorded before state`);
  }
  const door: DoorDefinition = { id: doorId, name: after.name, to: { map: after.to.map, arrival: after.to.arrival }, fields: after.fields };
  return { ...layer, doors: layer.doors.map((d, i) => (i === index ? door : d)) };
}

/**
 * Checks that no door id repeats within a map, across its door layers.
 *
 * @param map The map.
 * @param path The map's path, for the message.
 * @throws Error naming the repeated id.
 */
export function assertUniqueDoorIds(map: Pick<MapDocument, "layers">, path: string): void {
  const seen = new Set<string>();
  for (const layer of map.layers) {
    if (layer.kind !== "door") {
      continue;
    }
    for (const door of layer.doors) {
      if (seen.has(door.id)) {
        throw new Error(`${path} holds door ${door.id} twice; a door's id is unique within its map`);
      }
      seen.add(door.id);
    }
  }
}
